import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { ReferralWithScope_UserWithoutId, CacheKeys } from '../../helpers/DataStructures';
import {
  isArchivedInDashboardPreview,
  isStaleCreatedSentReferralInDashboardPreview,
} from '../../helpers/referralArchive';
import DateTimeFormat from '../../helpers/DateTimeFormat';
import { InHouseCardContent, InHouseCardHeader } from '../../components/CustomMUI';
import { translateFromStatusToShortTextForReferrer } from '../../helpers/Language';
import { getReferralUnreadCount } from '../../helpers/referralUnread';
import ReferralUnreadBadge from './ReferralUnreadBadge';
import Busy from '../../components/Busy';

const ReferralsAppReferralsOut: React.FC = () => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backTo = `${location.pathname}${location.search}${location.hash}`;

  const { isLoading, isError, data } = useQuery(
    [CacheKeys.REFERRALS_FROM_USER],
    () => apiServiceInstance.getUserReferralsOut(),
    { keepPreviousData: true }
  );

  // REQ-054: sent dashboard previews also hide stale created referrals based on conversation activity.
  const visibleReferrals = React.useMemo(() => {
    if (data === undefined) {
      return [];
    }

    return data.filter(
      (referral) =>
        !isArchivedInDashboardPreview(referral) &&
        !isStaleCreatedSentReferralInDashboardPreview(referral)
    );
  }, [data]);

  return (
    <>
      <Card style={{ margin: '0 auto' }} title="dd" variant="outlined">
        <InHouseCardHeader
          title={
            <Typography
              variant="h3"
              color="grey.50"
              sx={{ textAlign: 'center', width: '100%' }}
            >
              Live Referrals Sent -{' '}
              <MuiLink component={RouterLink} to="/dashboard/referrals/out">
                View All
              </MuiLink>
            </Typography>
          }
        />
        <InHouseCardContent style={{ textAlign: 'center' }}>
          {isError ? (
            // REQ-045: dashboard sent-referrals preview must show query failures instead of blank content.
            <Typography variant="body2">Unable to load sent referrals.</Typography>
          ) : isLoading || data === undefined ? (
            <Busy message="Loading referrals..." />
          ) : visibleReferrals.length === 0 ? (
            <Typography variant="body2">No active referrals sent yet</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  {visibleReferrals.map((referral: ReferralWithScope_UserWithoutId) => (
                    <TableRow
                      key={referral.id}
                      onClick={() =>
                        navigate(`/dashboard/referrals/out/${referral.id}`, {
                          state: { backTo },
                        })
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {referral.offer.user.companyName
                          ? referral.offer.user.companyName
                          : `${referral.offer.user.givenName} ${referral.offer.user.familyName}`}
                      </TableCell>
                      <TableCell>{DateTimeFormat(referral.created)}</TableCell>
                      <TableCell>{referral.theReferralName}</TableCell>
                      <TableCell>
                        <Stack spacing={0.75}>
                          <ReferralUnreadBadge count={getReferralUnreadCount(referral)} />
                          <Box
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              alignSelf: 'flex-start',
                            }}
                          >
                            <Typography component="div" variant="inherit">
                              {translateFromStatusToShortTextForReferrer(referral.status)}
                            </Typography>
                            {referral.status === 'succeeded' ? (
                              <Typography
                                component="div"
                                data-testid={`sent-referral-reward-${referral.id}`}
                                sx={{
                                  // REQ-021: center the earned fee under the success label rather than the whole column.
                                  color: 'primary.main',
                                  fontWeight: 700,
                                  textAlign: 'center',
                                }}
                                variant="body2"
                              >
                                +{'\u00A3'}{referral.offer.offeringFixedCashAmount}
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/dashboard/referrals/out/${referral.id}`, {
                                state: { backTo },
                              });
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InHouseCardContent>
      </Card>
    </>
  );
};

export default ReferralsAppReferralsOut;
